import { Children, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./SearchableSelect.css";

// Keeps the same controlled value/onChange contract as the selects it replaces.
// Typing filters choices; only choosing a result changes the stored value.
export default function SearchableSelect({ children, value = "", onChange, disabled, required, style, className = "", id, name, onBlur, ...props }) {
  const generatedId = useId(), inputId = id || `search-select-${generatedId}`, listId = `${inputId}-options`;
  const input = useRef(null), list = useRef(null), restoringFocus = useRef(false);
  const [open, setOpen] = useState(false), [search, setSearch] = useState(""), [active, setActive] = useState(0), [position, setPosition] = useState(null);
  const options = Children.toArray(children).filter(child => child?.type === "option").map(child => ({
    value: String(child.props.value ?? ""), label: Children.toArray(child.props.children).join(""), disabled: child.props.disabled,
  }));
  const selected = options.find(option => option.value === String(value));
  const needle = search.trim().toLocaleLowerCase("en-GB");
  const matches = options.filter(option => `${option.label} ${option.value}`.toLocaleLowerCase("en-GB").includes(needle));
  const expanded = open && !disabled;
  const close = () => { setOpen(false); setSearch(""); };
  const choose = option => {
    if (!option || option.disabled) return;
    onChange?.({ target: { value: option.value, name, id: inputId } });
    close(); restoringFocus.current = true; input.current?.focus(); restoringFocus.current = false;
  };
  useEffect(() => { setOpen(false); setSearch(""); }, [value, disabled]);
  useEffect(() => {
    input.current?.setCustomValidity(required && !String(value) ? "Choose an option from the list." : "");
  }, [required, value]);
  useLayoutEffect(() => {
    if (!expanded) return;
    const place = () => {
      const rect = input.current.getBoundingClientRect(), height = window.innerHeight, width = window.innerWidth;
      const below = height - rect.bottom - 8, above = rect.top - 8, upwards = below < 160 && above > below;
      setPosition({ position: "fixed", left: Math.max(8, Math.min(rect.left, width - Math.min(rect.width, width - 16) - 8)),
        width: Math.min(rect.width, width - 16), maxHeight: Math.max(44, Math.min(280, upwards ? above : below)),
        ...(upwards ? { bottom: height - rect.top + 4 } : { top: rect.bottom + 4 }) });
    };
    const outside = event => { if (!input.current?.contains(event.target) && !list.current?.contains(event.target)) close(); };
    place();
    window.addEventListener("resize", place); window.addEventListener("scroll", place, true); document.addEventListener("pointerdown", outside);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); document.removeEventListener("pointerdown", outside); };
  }, [expanded]);
  useEffect(() => { if (expanded) list.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView?.({ block: "nearest" }); }, [active, expanded]);
  const start = () => { if (!disabled) { setOpen(true); setSearch(""); setActive(0); } };
  const keyDown = event => {
    if (event.key === "Escape" && expanded) { event.preventDefault(); event.stopPropagation(); close(); }
    if (event.key === "Tab") close();
    if (["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      if (!expanded) start();
      else setActive(index => matches.length ? (index + (event.key === "ArrowDown" ? 1 : -1) + matches.length) % matches.length : 0);
    }
    if (event.key === "Enter" && expanded) { event.preventDefault(); event.stopPropagation(); choose(matches[active]); }
  };
  return <>
    <input {...props} ref={input} id={inputId} type="text" role="combobox" autoComplete="off" spellCheck={false}
      aria-autocomplete="list" aria-haspopup="listbox" aria-expanded={expanded} aria-controls={expanded ? listId : undefined}
      aria-activedescendant={expanded && matches[active] ? `${listId}-${active}` : undefined}
      data-value={value} required={required} disabled={disabled} className={`searchable-select ${className}`}
      style={style} value={expanded ? search : selected?.label || String(value || "")}
      placeholder={expanded ? "Search by name or number…" : selected?.label || "Select…"}
      onFocus={() => { if (!restoringFocus.current) start(); }} onClick={() => { if (!expanded) start(); }} onKeyDown={keyDown}
      onChange={event => { setSearch(event.target.value); setActive(0); setOpen(true); }}
      onBlur={event => { if (!list.current?.contains(event.relatedTarget)) close(); onBlur?.(event); }} />
    {name && <input type="hidden" name={name} value={value} disabled={disabled} />}
    {expanded && position && createPortal(<div ref={list} id={listId} role="listbox" aria-label={props["aria-label"] || "Matching choices"}
      className="searchable-select-options" style={position} onMouseDown={event => event.preventDefault()}>
      {matches.length ? matches.map((option, index) => <div id={`${listId}-${index}`} key={option.value} role="option" tabIndex={-1}
        data-index={index} aria-selected={option.value === String(value)} aria-disabled={!!option.disabled}
        className={index === active ? "searchable-select-active" : ""} onMouseEnter={() => setActive(index)} onClick={() => choose(option)}>{option.label}</div>) : <div role="status" className="searchable-select-empty">No matching lodges or bookings.</div>}
    </div>, document.body)}
  </>;
}
